import { createContext, ReactNode, useReducer } from 'react';
import { BlobEditor, BlobEditorStep, QuizQuestion } from '@myme/utils';
import { initialState } from './initialState';
import { reducer } from './reducer';
import { BlobEditorContext, BlobEditorContextValue, CreateBlobFormValues } from './types';
import { useEditBlobMutation } from './useEditBlobMutation';

export const Context = createContext<BlobEditorContextValue | undefined>(undefined);
interface ProviderProps {
  children: ReactNode;
  mockedInitialState?: BlobEditorContext;
}

export const BlobEditorProvider = ({ children, mockedInitialState }: ProviderProps) => {
  const [state, dispatch] = useReducer(reducer, mockedInitialState || initialState);
  const editBlobMutation = useEditBlobMutation(state.blob, dispatch);

  const editBlob = async () => editBlobMutation.mutate();

  const switchStep = (step: BlobEditorStep) => {
    dispatch({ type: 'SWITCH_STEP', payload: { step } });
  };

  const dispatchSetBlobFormIsDirty = () => {
    if (!state.isDirty) {
      dispatch({ type: 'BLOB_IS_DIRTY' });
    }
  };

  const dispatchSetInitialBlobData = (blob: BlobEditor) => {
    dispatch({ type: 'SET_INITIAL_BLOB_DATA', payload: blob });
  };

  const dispatchCreateBlob = (blob: CreateBlobFormValues) => {
    dispatch({ type: 'CREATE_BLOB', payload: blob });
  };

  const dispatchSetName = (name: string) => {
    dispatch({ type: 'SET_NAME', payload: name });
  };

  const dispatchSetMediaFile = (mediaLibraryId: string) => {
    dispatch({ type: 'SET_MEDIA_FILE', payload: mediaLibraryId });
  };

  const dispatchSetLink = (link: string) => {
    dispatch({ type: 'SET_LINK', payload: link });
  };

  const dispatchSetText = (text: string) => {
    dispatch({ type: 'SET_TEXT', payload: text });
  };

  const dispatchUpdateQuizQuestions = (questions: QuizQuestion[]) => {
    dispatch({ type: 'UPDATE_QUIZ_QUESTIONS', payload: questions });
  };

  const dispatchClearEditor = () => {
    dispatch({ type: 'CLEAR_EDITOR_STATE' });
  };

  return (
    <Context.Provider
      value={{
        editor: state,
        isEditing: editBlobMutation.isLoading,
        editBlob,
        switchStep,
        dispatchSetBlobFormIsDirty,
        dispatchSetInitialBlobData,
        dispatchCreateBlob,
        dispatchSetName,
        dispatchSetMediaFile,
        dispatchSetLink,
        dispatchSetText,
        dispatchUpdateQuizQuestions,
        dispatchClearEditor
      }}
    >
      {children}
    </Context.Provider>
  );
};
